import React from 'react';
import i18n from '@/i18n/config';
import { Chip } from '@heroui/react';
import {
  RiNotificationLine,
  RiTestTubeLine,
  RiAlertLine,
  RiUserLine,
  RiBuilding2Line,
  RiCloseLine
} from '@remixicon/react';

import { NotificationItem } from '@/types';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';

export type NotificationType =
  | 'system'
  | 'experiment'
  | 'alert'
  | 'admin_manual_broadcast_sent'
  | 'admin_system_alert';

interface NotificationTypeChipProps {
  type: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'dot';
  showIcon?: boolean;
}

const NOTIFICATION_TYPE_CONFIG: Record<
  NotificationType,
  {
    color: 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  system: { color: 'primary', label: i18n.t('copy.system'), icon: RiNotificationLine },
  experiment: { color: 'success', label: i18n.t('copy.experiment'), icon: RiTestTubeLine },
  alert: { color: 'warning', label: i18n.t('copy.alert'), icon: RiAlertLine },
  admin_manual_broadcast_sent: { color: 'secondary', label: i18n.t('copy.broadcast_sent'), icon: RiNotificationLine },
  admin_system_alert: { color: 'warning', label: i18n.t('copy.admin_alert'), icon: RiAlertLine }
};

function getNotificationTypeConfig(type: string) {
  if (type in NOTIFICATION_TYPE_CONFIG) {
    return NOTIFICATION_TYPE_CONFIG[type as NotificationType];
  }
  return {
    color: 'default' as const,
    label: type,
    icon: RiNotificationLine
  };
}

export function NotificationTypeChip({
  type,
  size = 'sm',
  variant = 'flat',
  showIcon = true
}: NotificationTypeChipProps) {
  const config = getNotificationTypeConfig(type);
  const IconComponent = config.icon;

  return (
    <Chip
      color={config.color}
      size={size}
      variant={variant}
      startContent={showIcon ? <IconComponent className="h-3 w-3" /> : undefined}
    >
      <small>{config.label}</small>
    </Chip>
  );
}

interface NotificationStatusChipProps {
  status: 'read' | 'unread';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'dot';
}

export function NotificationStatusChip({ status, size = 'sm', variant = 'flat' }: NotificationStatusChipProps) {
  const config =
    status === 'read' ? { color: 'default' as const, label: i18n.t('copy.read') } : { color: 'primary' as const, label: i18n.t('copy.unread') };

  return (
    <Chip color={config.color} size={size} variant={variant}>
      <small>{config.label}</small>
    </Chip>
  );
}

interface NotificationItemProps {
  notification: NotificationItem;
  onMarkAsRead?: (id: string) => void;
  showActions?: boolean;
  className?: string;
}

export function NotificationItemComponent({
  notification,
  onMarkAsRead,
  showActions = true,
  className = ''
}: NotificationItemProps) {
  const handleMarkAsRead = () => {
    if (notification.status === 'unread' && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 ${className} ${
        notification.status === 'unread'
          ? 'border-primary bg-primary-50 dark:bg-primary-950/20'
          : 'border-default-200 bg-default-50 dark:bg-default-100/5'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <NotificationTypeChip type={notification.type} />
            <NotificationStatusChip status={notification.status} />
          </div>

          <p className="text-foreground mb-2 text-sm break-words">{notification.message}</p>

          <div className="text-default-500 flex items-center gap-4 text-xs">
            <DateTimeDisplay dateString={notification.created_at} />

            {notification.target_user && (
              <div className="flex items-center gap-1">
                <RiUserLine className="h-3 w-3" />
                <span>
                  {notification.target_user.first_name} {notification.target_user.last_name}
                </span>
              </div>
            )}

            {notification.target_organization && (
              <div className="flex items-center gap-1">
                <RiBuilding2Line className="h-3 w-3" />
                <span>{notification.target_organization.name}</span>
              </div>
            )}
          </div>
        </div>

        {showActions && notification.status === 'unread' && (
          <button
            onClick={handleMarkAsRead}
            className="bg-primary text-primary-foreground hover:bg-primary-600 rounded-md px-3 py-1 text-xs transition-colors"
          >
            Mark as Read
          </button>
        )}
      </div>
    </div>
  );
}

interface NotificationToastProps {
  notification: NotificationItem;
  onClose?: () => void;
  autoClose?: boolean;
  duration?: number;
}

export function NotificationToast({
  notification,
  onClose,
  autoClose = true,
  duration = 5000
}: NotificationToastProps) {
  React.useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-background border-default-200 animate-in slide-in-from-right-full rounded-lg border p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-2">
              <NotificationTypeChip type={notification.type} size="sm" />
            </div>
            <p className="text-foreground mb-2 text-sm">{notification.message}</p>
            <div className="text-default-500 text-xs">
              <DateTimeDisplay dateString={notification.created_at} />
            </div>
          </div>

          {onClose && (
            <button onClick={onClose} className="text-default-400 hover:text-default-600 transition-colors">
              <RiCloseLine className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
